import { ArrowDown, ArrowUp, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BrandButton } from "@/components/PageHeader";
import { TYPE_LABEL } from "@/lib/formOps";

const IconBtn = ({ icon: Icon, label, onClick, disabled, testId, danger }) => (
  <button type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label} data-testid={testId}
    className={`w-8 h-8 grid place-items-center rounded-md border border-[#E5E7EB] bg-white transition-colors disabled:opacity-30 disabled:pointer-events-none ${danger ? "hover:text-[#E60000] hover:border-[#E60000]" : "hover:border-[#111]"}`}>
    <Icon className="w-3.5 h-3.5" />
  </button>
);

const FieldRow = ({ f, locked, first, last, onMove, onEdit, onRemove }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-md border border-[#E5E7EB] bg-white px-3 py-2.5" data-testid={`builder-field-${f.key}`}>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-[#111]">
        {locked && <Lock className="w-3.5 h-3.5 text-gray-400 shrink-0" />}<span className="truncate">{f.label}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[11px]">
        <span className="rounded bg-[#F3F4F6] px-1.5 py-0.5 text-gray-600">{TYPE_LABEL[f.type] || f.type}</span>
        <span className={`rounded px-1.5 py-0.5 ${f.required ? "bg-[#FFF0F0] text-[#E60000]" : "bg-[#F3F4F6] text-gray-500"}`}>{f.required ? "Required" : "Optional"}</span>
        {!f.builtin && <span className="rounded bg-blue-50 text-blue-700 px-1.5 py-0.5">Custom</span>}
      </div>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <IconBtn icon={ArrowUp} label="Move up" onClick={() => onMove(-1)} disabled={first} testId={`builder-field-up-${f.key}`} />
      <IconBtn icon={ArrowDown} label="Move down" onClick={() => onMove(1)} disabled={last} testId={`builder-field-down-${f.key}`} />
      <IconBtn icon={Pencil} label="Edit field" onClick={onEdit} testId={`builder-field-edit-${f.key}`} />
      <IconBtn icon={Trash2} label={locked ? "Core fields cannot be removed" : "Remove field"} onClick={onRemove} disabled={locked} danger testId={`builder-field-remove-${f.key}`} />
    </div>
  </div>
);

export const SectionCard = ({ s, si, count, locked, fixed, pool, ops }) => {
  const hasLocked = s.fields.some((f) => fixed.includes(f.key));
  return (
    <section className="rounded-lg border border-[#E5E7EB] bg-[#F8F9FA] p-4 sm:p-5" data-testid={`builder-section-${si}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <span className="font-mono text-xs font-bold text-[#E60000] shrink-0">{String(si + 1).padStart(2, "0")}</span>
        <Input value={s.title} onChange={(e) => ops.rename(si, e.target.value)} placeholder="Section title" className="h-10 bg-white font-heading font-bold flex-1" data-testid={`builder-section-title-${si}`} />
        <div className="flex items-center gap-1.5 shrink-0">
          <IconBtn icon={ArrowUp} label="Move section up" onClick={() => ops.moveSection(si, -1)} disabled={si === 0} testId={`builder-section-up-${si}`} />
          <IconBtn icon={ArrowDown} label="Move section down" onClick={() => ops.moveSection(si, 1)} disabled={si === count - 1} testId={`builder-section-down-${si}`} />
          <IconBtn icon={Trash2} label={hasLocked ? "Move the core fields out before deleting this section" : "Delete section"} onClick={() => ops.removeSection(si)} disabled={hasLocked || count === 1} danger testId={`builder-section-remove-${si}`} />
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {s.fields.map((f, fi) => (
          <FieldRow key={f.key} f={f} locked={fixed.includes(f.key)} first={si === 0 && fi === 0} last={si === count - 1 && fi === s.fields.length - 1}
            onMove={(d) => ops.moveField(si, fi, d)} onEdit={() => ops.edit(si, f)} onRemove={() => ops.removeField(si, fi)} />
        ))}
        {!s.fields.length && <div className="rounded-md border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">No fields yet. Add one below.</div>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <BrandButton variant="outline" onClick={() => ops.edit(si, null)} data-testid={`builder-add-field-${si}`}><Plus className="w-4 h-4" />Add field</BrandButton>
        {pool.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><BrandButton variant="outline" data-testid={`builder-add-builtin-${si}`}><Plus className="w-4 h-4" />Add back a built-in field</BrandButton></DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {pool.map((b) => <DropdownMenuItem key={b.key} onSelect={() => ops.addBuiltin(si, b)} data-testid={`builder-builtin-option-${b.key}`}>{b.label}</DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </section>
  );
};
